import React, { useContext } from 'react';
import { StaticContext } from '@sm/webassets/StaticContext';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import useStyles from './useStyles';
import { treatment1Mweb1, treatment1Mweb2 } from '~static/images/optionalMobilePaths';
import { OptionalPathsCopyType } from '~helpers/types';
import OptionalPathCard from './components/OptionalPathCard';
import ColoredLogoWithText from '~app/components/surveyThanksLogo/ColoredLogoWithText';
import { UT_SOURCE_PATHS } from '~app/components/constants';

type BaseCardProps = {
  image: string;
  imageAlt: string;
  header: string;
  subHeader: string;
  ctaText: string;
  ctaType: 'creator' | 'taker';
  pathImageClass: string;
};

// Copy to be translated when these experimental endpages are exposed to all locales
const COPY: OptionalPathsCopyType = {
  banner: 'Thank you for taking this survey.',
  header: 'Discover what else you can do with SurveyMonkey',
  creatorAlt: 'Survey creator',
  creatorHeader: 'Create unlimited forms and surveys for free',
  creatorSubHeader:
    'Send surveys to anyone, from friends to customers. Use insights to make better, data-driven decisions.',
  creatorCTA: 'Create surveys',
  takerAlt: 'Survey taker',
  takerHeader: 'Take more surveys and earn rewards',
  takerSubHeader:
    'Earn credits each time you take a survey. Redeem them for gift cards or donate the money to charity.',
  takerCTA: 'Take more surveys',
};

function OptionalMobilePaths({ utSource2 }: { utSource2: string }): React.ReactElement {
  const utSource = 'survey-thanks';
  const utSource3SignUp = 'MobileSignUp';

  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
  } = useContext(StaticContext);

  const { pathImage, pageContent, mainGrid } = useStyles();

  // Determine path for swapping image and content order
  const isFirstPath =
    utSource2 === UT_SOURCE_PATHS?.OPTIONAL_PATH_MWEB1 || utSource2 === UT_SOURCE_PATHS?.OPTIONAL_PATH_MAPP1;

  const ctaCardData: BaseCardProps[] = [
    {
      image: treatment1Mweb1,
      imageAlt: COPY.creatorAlt,
      header: COPY.creatorHeader,
      subHeader: COPY.creatorSubHeader,
      ctaText: COPY.creatorCTA,
      ctaType: 'creator',
      pathImageClass: pathImage,
    },
    {
      image: treatment1Mweb2,
      imageAlt: COPY.takerAlt,
      header: COPY.takerHeader,
      subHeader: COPY.takerSubHeader,
      ctaText: COPY.takerCTA,
      ctaType: 'taker',
      pathImageClass: pathImage,
    },
  ];

  // Reverse the order if `isFirstPath` is false
  const orderedCtaCardData = isFirstPath ? ctaCardData : [...ctaCardData].reverse();

  return (
    <>
      <ColoredLogoWithText
        utSource={utSource}
        utSource2={utSource2}
        isThanksText
        amplitudeEvent="optional paths contribute cta"
      />
      <Box pt={4} pb={2} pr={2} pl={2} className={pageContent}>
        <Box pt={4}>
          <Typography variant="cardTitle" weight="medium" align="center" component="h1">
            {COPY.header}
          </Typography>
        </Box>
        <div className={mainGrid}>
          {orderedCtaCardData.map(cardData => (
            <OptionalPathCard
              key={cardData.ctaText}
              image={cardData.image}
              imageAlt={cardData.imageAlt}
              header={cardData.header}
              subHeader={cardData.subHeader}
              ctaText={cardData.ctaText}
              ctaType={cardData.ctaType}
              templateType={templateType}
              surveyId={surveyId}
              collectorId={collectorId}
              respondentId={respondentId}
              utSource={utSource}
              utSource2={utSource2}
              utSource3={utSource3SignUp}
              pathImageClass={cardData.pathImageClass}
            />
          ))}
        </div>
      </Box>
    </>
  );
}

export default OptionalMobilePaths;
